/**
 * Network and communication types
 * Contains types for NATS, WebSocket, and network configuration
 */

import type { ChatType } from './chat.types';

// ========== NATS Types ==========

export type NatsMessageType = 'message' | 'admin-message';

export type NatsConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'reconnecting' | 'closed' | 'error';

export type NatsConnectionSource = 'client' | 'agent' | 'dashboard';

// ========== Network Configuration ==========

export const NETWORK_CONFIG = {
  SHARED_CLOSE_DELAY_MS: 3000,
  CONNECT_TIMEOUT_MS: 10_000,
  RECONNECT_TIME_WAIT_MS: 2000,
  PING_INTERVAL_MS: 30_000,
  MAX_PING_OUT: 3,
  DEFAULT_MESSAGE_LIMIT: 50,
  POLL_MESSAGE_LIMIT: 10,
  RETRY_INITIAL_DELAY_MS: 1000,
  RETRY_MAX_DELAY_MS: 30_000,
  RETRY_BACKOFF_MULTIPLIER: 2,
} as const;

export type NetworkConfig = typeof NETWORK_CONFIG;

// ========== Chunk Data Types ==========

export interface ChunkData {
  sequenceId?: number;
  /** JetStream stream sequence, populated from JsMsg.info.streamSequence when delivered via JetStream. */
  streamSeq?: number;
  /** Authoritative dialog streaming state at the time this chunk was emitted; consumers should mirror it into their dialog cache. */
  streamState?: 'IDLE' | 'STREAMING';
  type: string;
  text?: string;
  integratedToolType?: string;
  toolFunction?: string;
  /** Execution chunks carry the human-readable title as `title`. */
  title?: string;
  /** EXECUTING_TOOL chunks carry the tool's human-readable explanation (what/why). */
  toolExplanation?: string;
  parameters?: Record<string, unknown>;
  result?: string;
  success?: boolean;
  toolExecutionRequestId?: string;
  error?: string;
  details?: string;
  approvalRequestId?: string;
  approval_request_id?: string;
  approvalType?: string;
  command?: string;
  explanation?: string;
  approved?: boolean;
  /** On APPROVAL_RESULT chunks: display name of the user who resolved the request. */
  resolvedByName?: string;
  /** Batch-approval rows. Coerced by `normalizeToolCalls` in the NATS decoder
   *  — the wire shape is per-backend, so entries stay opaque here. */
  toolCalls?: unknown[];
  /** Opaque per-chunk payload some backends attach beside `text`. */
  payload?: Record<string, unknown>;
  modelName?: string;
  providerName?: string;
  provider?: string;
  contextWindow?: number;
  ownerType?: string;
  displayName?: string;
  /** Chunk kinds this lib does not model yet — decoders read them through
   *  their own typeof gates. */
  [key: string]: unknown;
}

export interface BufferedChunk {
  chunk: ChunkData;
  messageType: NatsMessageType;
}

// ========== WebSocket Types ==========

export interface WebSocketMessage {
  type: string;
  payload: unknown;
  timestamp?: string;
  id?: string;
}

export interface WebSocketConfig {
  url: string;
  protocols?: string | string[];
  reconnect?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  onOpen?: (event: Event) => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
  onMessage?: (event: MessageEvent) => void;
}

// ========== Fetch Functions ==========

export type FetchChunksFunction = (
  dialogId: string,
  chatType: ChatType,
  fromSequenceId?: number | null,
) => Promise<ChunkData[]>;

// ========== Network Response Types ==========

export interface NetworkResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T = unknown> extends NetworkResponse<T> {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

// ========== API Error Types ==========

export interface NetworkError {
  code: string;
  message: string;
  details?: unknown;
  timestamp: string;
}
